import React, { useMemo, useState, useEffect } from 'react';
import { Profile, Text, Icon } from '@/ui-kits';
import { useDispatch, useSelector } from 'react-redux';
import { authDataSelector, dashboardSearchTerm, totalUnreadNotifications, hospitalAdminProfileSelector } from '@/services/redux/selectors';
import { logoutRequest } from '@/services/redux/slices/authSlice';
import { setDashboardSearchTerm } from '@/services/redux/slices';
import { useClickOutside, useRole, useWindowDimensions } from '@/hooks';
import { DashboardRouteHeaderTitles, Routes, getNormalizedRoute } from '@/navigation/Routes';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { formatName, cn } from '@/utils/common_functions';
import { debounceTimer } from '@/constants';
import { NotificationModal } from '@/components/common/NotificationModal';

interface DashboardHeaderProps {
  onSideBarOpen?: () => void;
}

const RoutesWithBackButton = new Set([
  Routes.CONSULTATION_SUMMARY,
  Routes.SPECIALIST_PROFILE_VIEW,
  Routes.SPECIALIST_ADMIN_PROFILE_DETAIL,
  Routes.PROFILE,
  Routes.HOSPITAL_PROFILE_VIEW,
  Routes.FAVORITES,
]);

const RoutesWithSearchBar: Set<string> = new Set([
  Routes.SPECIALISTS,
  Routes.CONSULTATION,
  Routes.FAVORITES,
]);

export const DashboardHeader: React.FC<DashboardHeaderProps> = props => {
  const { onSideBarOpen } = props;
  const { pathname } = useLocation();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const authData = useSelector(authDataSelector);
  const [isOpen, setIsOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const ref = useClickOutside<HTMLButtonElement>(() => setIsOpen(false));
  const notificationRef = useClickOutside<HTMLDivElement>(() => setIsNotificationOpen(false));
  const unreadCount = useSelector(totalUnreadNotifications);
  const { isPatient, isSpecialist, isSupportAdmin, isHospitalPoc } = useRole();
  const { width } = useWindowDimensions();
  const hospitalAdminProfile = useSelector(hospitalAdminProfileSelector);

  const dispatch = useDispatch();

  const searchPlaceholder = {
    [Routes.SPECIALISTS]: "Search by Specialist Name or Hospital Name",
    [Routes.CONSULTATION]: `Search by ${isSpecialist ? "Patient" : "Specialist"} Name or Case ID`,
    [Routes.FAVORITES]: "Search by Specialist Name",
  }

  // Search state for specialist consultations page
  const showSearchBar = RoutesWithSearchBar.has(pathname);
  const searchTermFromRedux = useSelector(dashboardSearchTerm);
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTermFromRedux);

  const handleLogout = () => {
    dispatch(logoutRequest());
  };


  const showBackButton = useMemo(() => {
    const normalizedRoute = getNormalizedRoute(pathname);

    if (normalizedRoute === Routes.PROFILE && isHospitalPoc) {
      return searchParams.get('mode') === 'edit' && hospitalAdminProfile?.profile_completed === true;
    }

    // For other profile routes (patient, specialist): show back button in edit mode
    if (normalizedRoute === Routes.PROFILE) {
      return searchParams.get('mode') === 'edit';
    }

    // For other routes, use the existing logic
    for (const route of RoutesWithBackButton) {
      if (normalizedRoute === route) {
        return true;
      }
    }
    return false;
  }, [pathname, searchParams, isHospitalPoc, hospitalAdminProfile]);

  const dashboardHeaderTitle = useMemo(() => {
    const normalizedRoute = getNormalizedRoute(pathname);

    // override for one route
    if (normalizedRoute == Routes.PROFILE) {
      return isHospitalPoc ? "Hospital Profile" : "My Profile"
    }

    // Show "Specialist Profile : {id}" for profile detail route
    if (normalizedRoute === Routes.SPECIALIST_ADMIN_PROFILE_DETAIL && id) {
      return `Specialist Profile`;
    }

    return DashboardRouteHeaderTitles[normalizedRoute as keyof typeof DashboardRouteHeaderTitles] ?? 'Dashboard';
  }, [pathname, id, isHospitalPoc]);

  // Sync local state with Redux when Redux changes externally
  useEffect(() => {
    setLocalSearchTerm(searchTermFromRedux);
  }, [searchTermFromRedux]);

  // Debounced search handler
  useEffect(() => {
    if (!showSearchBar) return;

    const debounce = setTimeout(() => {
      if (localSearchTerm !== searchTermFromRedux) {
        dispatch(setDashboardSearchTerm(localSearchTerm));
      }
    }, debounceTimer); // 300ms debounce

    return () => {
      clearTimeout(debounce);
    };
  }, [localSearchTerm, dispatch, searchTermFromRedux, showSearchBar]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalSearchTerm(e.target.value);
  };

  const isSpecialistDisplayHeader = pathname === Routes.SPECIALIST_DISPLAY && width < 640; // keep in same row on xl and above, allow wrapping below xl

  return (
    <div
      className={cn(
        // On Specialist Display: keep icons in the same row; only title wraps within available space
        isSpecialistDisplayHeader ? 'flex flex-nowrap items-start' : 'flex flex-wrap sm:flex-nowrap items-center',
        'py-4 sm:py-6 px-2 sm:px-6 gap-2 sm:gap-4 xl:gap-6 border-b-ui_input_border',
      )}>

      {/* menu button */}
      <button
        onClick={onSideBarOpen}
        className={cn(
          "xl:hidden flex-shrink-0",
          isSpecialistDisplayHeader && "mt-3.5",
        )}>
        <Icon type="menu" className="size-4" />
      </button>

      {/* back button */}
      {showBackButton && (
        <button onClick={() => pathname === Routes.FAVORITES ? navigate(Routes.SPECIALISTS) : navigate(-1)} className="flex-shrink-0">
          <Icon type="arrow_left" className="size-5 sm:size-6 font-bold text-ui_blue" />
        </button>
      )}

      {/* title */}
      <Text variant="heading" className={cn(
        "xl:text-4xl text-ui_blue grow ml-1 sm:ml-0",
        pathname === Routes.ADMIN ? "sm:!text-4xl" : (pathname === Routes.RBAC ? "sm:!text-4xl" : "sm:!text-4xl"),
        // Allow wrapping on Specialist Display so it doesn't overlap bell/profile on small widths
        pathname === Routes.SPECIALIST_DISPLAY
          ? "text-base min-[320px]:text-2xl sm:text-xl xl:text-4xl whitespace-normal break-words leading-tight"
          : "whitespace-nowrap"
      )}>
        {dashboardHeaderTitle}
      </Text>

      {/* search bar, notification button, profile button */}
      {(isPatient || isSpecialist) && showSearchBar && <div className={cn("lg:flex debug hidden gap-2 bg-white items-center min-w-0 p-2 rounded-lg border border-ui_input_border_dark/40")}>
        <Icon type="search_blue" className="size-4 pointer-events-none" />
        <input
          type="text"
          placeholder={searchPlaceholder[pathname as keyof typeof searchPlaceholder]}
          value={localSearchTerm}
          onChange={handleSearchChange}
          className="w-[280px] lg:w-[320px] xl:w-[380px] max-w-full outline-none text-sm font-FigtreeRegular placeholder:font-FigtreeRegular"
        />
      </div>}

      {/* Favorites icon - show on all screens for patients */}
      {isPatient && (
        <button
          onClick={() => navigate(Routes.FAVORITES)}
          className="p-2 border-ui_input_border_dark/40 rounded-lg hover:bg-gray-50 transition-colors flex-shrink-0"
          title={pathname === Routes.FAVORITES ? "Favorites" : "View Favorites"}
        >
          <Icon type={pathname === Routes.FAVORITES ? "heart_red" : "heart_empty"} className="size-5 sm:size-[30px] cursor-pointer" />
        </button>
      )}

      {/* notification button */}
      <div className={cn("flex items-center flex-shrink-0 relative", isSpecialistDisplayHeader && "mt-3.5")} ref={notificationRef}>
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsNotificationOpen(!isNotificationOpen);
          }}
          className="relative">
          <Icon type="bell" className="size-5 sm:size-[30px] cursor-pointer" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full min-w-[20px] h-5 px-1 flex items-center justify-center font-FigtreeBold z-[9]">
              {unreadCount}
            </span>
          )}
        </button>

        {/* notification dropdown */}
        {isNotificationOpen && (
          <NotificationModal open={isNotificationOpen} onClose={() => setIsNotificationOpen(false)} />
        )}
      </div>

      {/* profile button */}
      <button tabIndex={0} onClick={() => setIsOpen(p => !p)} ref={ref} className={cn("relative flex-shrink-0 ml-0.5 sm:ml-0", isSpecialistDisplayHeader && "mt-3")}>
        <Profile className="size-8 sm:size-[50px]" />

        {/* profile dropdown */}
        {isOpen && (
          <div className="flex flex-col items-center gap-2 top-[100%] right-0 z-10 bg-white rounded-md shadow-md absolute min-w-[200px] sm:min-w-[240px]">
            <div className="flex flex-col px-3 sm:px-4 py-2 gap-1 items-center border-b-[1px] border-b-ui_input_border w-full">
              <Text className="font-FigtreeMeduim text-lg sm:text-xl text-ui_blue truncate max-w-full">{formatName(authData?.username ?? '')}</Text>
              <Text className="font-FigtreeRegular text-sm sm:text-base text-ui_blue opacity-90 truncate max-w-full">{authData?.email_id}</Text>
            </div>
            <button onClick={handleLogout} className="flex items-center gap-1 p-2 cursor-pointer">
              <Icon type="logout" />
              <Text className="font-FigtreeRegular text-sm sm:text-base text-rejection">Logout</Text>
            </button>
          </div>
        )}
      </button>
    </div>
  );
};
